import { useState } from "react";
import { ShoppingCart, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { UnitPills } from "./UnitPills";

/**
 * Pulsante "Ordina" da usare nella riga ingrediente: apre un dialog con
 * quantità + UM e aggiunge la riga al carrello ordini persistente.
 */
export function OrderIngredientButton({ ingredient, compact = false }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                data-testid={`order-ingredient-btn-${ingredient.id}`}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpen(true);
                }}
                title="Aggiungi al carrello ordini"
                className={
                    compact
                        ? "inline-flex items-center justify-center h-9 w-9 rounded-full bg-brand-gold/15 text-brand-gold hover:bg-brand-gold hover:text-brand-navy transition shrink-0"
                        : "inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-navy text-[12px] font-bold px-3 py-1.5 hover:bg-brand-goldDark transition shadow-gold"
                }
            >
                <ShoppingCart size={compact ? 15 : 13} strokeWidth={compact ? 2.2 : 2.4} />
                {!compact && <span>Ordina</span>}
            </button>
            {open && (
                <OrderQtyDialog
                    ingredient={ingredient}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
}

function OrderQtyDialog({ ingredient, onClose }) {
    const [quantity, setQuantity] = useState(1);
    const [unit, setUnit] = useState(ingredient.unit);
    const [busy, setBusy] = useState(false);
    const [warn, setWarn] = useState("");
    const navigate = useNavigate();

    const submit = async (e) => {
        e?.preventDefault();
        const q = parseFloat(quantity);
        if (!q || q <= 0) {
            setWarn("Inserisci una quantità maggiore di zero");
            return;
        }
        setBusy(true);
        setWarn("");
        try {
            const { data: addResp } = await api.post("/orders/cart/add", {
                lines: [{
                    ingredient_id: ingredient.id,
                    quantity: q,
                    unit,
                    source: "manual",
                }],
            });
            const merged = Array.isArray(addResp?.merged) && addResp.merged.length > 0
                ? addResp.merged[0]
                : null;
            // Verifica se esiste un fornitore con prezzo per questo ingrediente
            let hasSupplier = false;
            try {
                const { data } = await api.get(
                    `/supplier-prices?ingredient_id=${ingredient.id}`
                );
                hasSupplier = Array.isArray(data) && data.length > 0;
            } catch {
                // ignore
            }
            if (merged) {
                toast.success(
                    `${ingredient.name}: era già nel carrello — ora totale ${merged.total_quantity} ${merged.unit}`,
                    {
                        action: {
                            label: "Vedi ordini",
                            onClick: () => navigate("/orders"),
                        },
                    }
                );
            } else if (hasSupplier) {
                toast.success(
                    `${ingredient.name} aggiunto al carrello ordini`,
                    {
                        action: {
                            label: "Vedi ordini",
                            onClick: () => navigate("/orders"),
                        },
                    }
                );
            } else {
                toast.warning(
                    `${ingredient.name} aggiunto al carrello — nessun fornitore associato`,
                    {
                        description:
                            "Apri /ordini per associarlo manualmente, oppure crea il listino in /fornitori.",
                        action: {
                            label: "Apri ordini",
                            onClick: () => navigate("/orders"),
                        },
                    }
                );
            }
            onClose();
        } catch (err) {
            setWarn(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="order-qty-dialog"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
            >
                <div className="px-6 py-4 border-b border-brand-line flex items-center justify-between gap-3">
                    <div>
                        <div className="eyebrow text-brand-gold">Aggiungi al carrello</div>
                        <h3 className="font-display text-2xl text-brand-ink mt-0.5 truncate">
                            {ingredient.name}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-9 w-9 rounded-full text-brand-muted hover:bg-brand-cream transition flex items-center justify-center shrink-0"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="px-6 py-5 space-y-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1.5">
                            Quantità
                        </label>
                        <input
                            type="number"
                            step="0.001"
                            min="0"
                            value={quantity}
                            autoFocus
                            onChange={(e) => setQuantity(e.target.value)}
                            data-testid="order-qty-input"
                            className="w-full px-3 py-2.5 rounded-md bg-white border border-brand-line text-base focus:outline-none focus:border-brand-gold"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-brand-muted mb-1.5">
                            Unità di misura
                        </label>
                        <UnitPills value={unit} onChange={setUnit} />
                        <div className="mt-1 text-[11px] text-brand-muted">
                            UM base ingrediente: <strong>{ingredient.unit}</strong>
                        </div>
                    </div>
                    {warn && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-[12px] px-3 py-2 flex items-start gap-2">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>{warn}</span>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="order-qty-confirm"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-60"
                    >
                        <ShoppingCart size={14} /> {busy ? "Aggiungo…" : "Aggiungi al carrello"}
                    </button>
                </div>
            </form>
        </div>
    );
}
